import { useEffect, useState } from "react";
import { Button, Input, Switch } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate } from "react-router-dom";
import { OpenAIOutlined } from "@ant-design/icons";
import apiClient from "../../actions/api";
import isEmpty from "../../validations/is-empty";
import openNotification from "../../components/Notifications/openNotification";
import { saveBid } from "../../actions/bid";

// import style
import "../../style/bid/bid.scss";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const { TextArea } = Input;

const AutoBid = ({ title }) => {
  // set title
  document.title = title;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth.user);

  const [jobDesc, setjobDesc] = useState(""); // set job description value
  const [jobUrl, setJobUrl] = useState(""); // set job url value
  const [status, setStatus] = useState(true);
  const [model, setModel] = useState(true); // true: claudeAI, false: openAI
  const [urlStatus, setUrlStatus] = useState(true);
  const [loading, setLoading] = useState(false);
  const [aiDesc, setAIDesc] = useState("");

  useEffect(() => {
    setLoading(true);

    if (jobDesc.trim().length !== 0) {
      setStatus(true);
    }

    if (jobUrl.trim().length !== 0) {
      setUrlStatus(true);
    }

    setLoading(false);
  }, [jobDesc, jobUrl]);

  const handleClear = () => {
    setjobDesc("");
    setStatus(false);
    setJobUrl("");
    setUrlStatus(false);
  };

  const handleGenerateDraft = async () => {
    if (jobUrl.trim().length === 0) {
      setUrlStatus(false);
      return;
    }

    if (aiDesc.trim().length === 0) {
      setStatus(false);
      return;
    }

    if (isEmpty(user.template_url)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please upload resume template.",
      );
      return;
    }

    const params = {
      user: user._id,
      job_url: jobUrl.trim(),
      job_desc: aiDesc.trim(),
    };

    setLoading(true);
    await apiClient
      .post("/bids/gen-draft", params)
      .then((res) => {
        if (!isEmpty(res.data)) {
          dispatch(saveBid(res.data));
          navigate("/resume-preview");
          openNotification(
            "success",
            "Success",
            "You've generated Draft for resume.",
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  const onChangeModel = (checked) => {
    setModel(checked);
  };

  const getTextContent = async (url) => {
    if (!isEmpty(url)) {
      setLoading(true);
      apiClient
        .post("/bids/job-content", { url: url })
        .then((res) => {
          if (!isEmpty(res.data)) {
            setAIDesc(res.data);
          }
          setLoading(false);
        })
        .catch((err) => {
          if (!isEmpty(err?.response?.data?.errors)) {
            err.response.data.errors.forEach((item) => {
              openNotification("error", "Validation Error!", item.msg);
            });
          }
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    getTextContent(jobUrl);
  }, [jobUrl]);

  return (
    <div className="bidSection">
      <h2 className="title">
        <OpenAIOutlined />
        &nbsp;Job Bidding
      </h2>

      <div className="bid-field">
        <label className="label">Choose model</label>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 10,
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <span>OpenAI</span>
          <Switch defaultChecked={model} onChange={onChangeModel} />
          <span>ClaudeAI</span>
        </div>
      </div>

      <div className="bid-field">
        <label className="label">
          Job Url&nbsp;<span>*</span>
        </label>
        <Input
          status={urlStatus ? "" : "error"}
          rows={8}
          placeholder="Paste the job url here"
          value={jobUrl}
          onChange={(e) => setJobUrl(e.target.value)}
        />
      </div>

      <div className="bid-field">
        <label className="label">
          AI Description&nbsp;<span>*</span>
        </label>
        <TextArea
          status={status ? "" : "error"}
          rows={8}
          placeholder="Paste the job description here"
          value={aiDesc}
          onChange={(e) => setAIDesc(e.target.value)}
        />
      </div>

      <div className="bid-action">
        <Button
          type="primary"
          htmlType="button"
          onClick={handleGenerateDraft}
          loading={loading}
        >
          Generate Resume Draft
        </Button>
        <Button
          type="default"
          htmlType="button"
          onClick={handleClear}
          loading={loading}
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export default AutoBid;
